import React, { useState, useEffect, useRef } from 'react';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min';
import SignatureModal from './SignatureModal/SignatureModal';
import axiosInstance from '../../../../../api';

// Modal component for managing patient situation data
const PatientSituationModal = ({ onSave }) => {
  // Helper function to get current time in HH:MM AM/PM format
  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // State for form data, including all patient situation fields
  const [formData, setFormData] = useState({
    clinical_condition: '',
    past_medical_history: [],
    past_medical_history_details: { 'Allergy to': '', 'Others': '' },
    vital_signs: {
      pulse: '',
      spo2: '',
      blood_pressure: '',
      respiration: '',
      glucose_test: '',
      time: getCurrentTime(),
    },
    additional_vital_signs: [],
    level_of_consciousness: [],
    medication_during_transfer: [{ medication_name: '', dose: '', time: '' }],
    infectious_precautions: [],
    s_situation: '',
    b_background: '',
    a_assessment: '',
    r_recommendation: '',
    status_during_transport: '',
    handover_vital_signs: {
      temperature: '',
      pulse: '',
      blood_pressure: '',
      respiration: '',
      glucose_test: '',
    },
    handover_acknowledgment_type: 'Automatically',
    acknowledged_received_items: [],
    received_by: '',
    received_position: '',
    received_id_number: '',
    manual_ackn_nurse_name: '',
    manual_ackn_signature: null,
    signature: null,
    mt_signature: null,
    medical_staff_involved: [{ role: '', signature: null }],
    ems_action_plan: [],
  });

  // State for tracking signature modal type and index
  const [signatureModalType, setSignatureModalType] = useState(null);
  const [signatureModalIndex, setSignatureModalIndex] = useState(null);
  // State for managing medical staff rows
  const [staffRows, setStaffRows] = useState([{ id: 1 }]);
  // State for tracking the number of vital sign entries
  const [vitalSignCount, setVitalSignCount] = useState(1);
  // Retrieve case ID from session storage
  const caseId = sessionStorage.getItem('currentCaseId');
  // Reference for patient modal
  const patientModalRef = useRef(null);
  // Determine if the form is editable based on user role and API check
  const sessionRole = sessionStorage.getItem('Role_Name');
  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editCheckLoading, setEditCheckLoading] = useState(true);
  // Default vital signs that should not have a delete icon
  const defaultVitalSigns = ['pulse', 'spo2', 'blood_pressure', 'respiration', 'glucose_test', 'time'];

  // Check if EMS Team can edit the form
  useEffect(() => {
    const checkEditAvailability = async () => {
      if (sessionRole === 'EMS Team' && caseId) {
        try {
          const response = await axiosInstance.get(`/ems/is-available-for-edit/${caseId}`, {
            headers: {
              'Accept': 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
            }
          });
          
          // If status is true, allow editing; if false, disable editing
          setIsEditable(response.data.status === true);
        } catch (error) {
          console.error('Error checking edit availability:', error);
          // In case of error, disable editing for safety
          setIsEditable(false);
        } finally {
          setEditCheckLoading(false);
        }
      } else {
        // For non-EMS Team roles, maintain original logic
        setIsEditable(sessionRole === 'EMS Team');
        setEditCheckLoading(false);
      }
    };

    checkEditAvailability();
  }, [caseId, sessionRole]);

  // Fetch patient situation data when caseId changes
  useEffect(() => {
    const fetchPatientSituation = async () => {
      try {
        const response = await axiosInstance.get(`/ems/get-patient-situation/${caseId}`);
        const data = response.data;
        let med_transfer = data.medication_during_transfer || [];
        if (!med_transfer.length) med_transfer = [{ medication_name: '', dose: '', time: '' }];
        let staff = data.medical_staff_involved || [];
        if (!staff.length) staff = [{ role: '', signature: null }];

        // Determine acknowledgment type based on API response
        let acknowledgmentType = 'Automatically';
        if (data.manual_ackn_nurse_name || data.manual_ackn_signature) {
          acknowledgmentType = 'Manually';
        }

        setFormData({
          clinical_condition: data.clinical_condition || '',
          past_medical_history: data.past_medical_history?.map((item) => item.condition) || [],
          past_medical_history_details: data.past_medical_history_details || { 'Allergy to': '', 'Others': '' },
          vital_signs: {
            pulse: data.vital_signs?.pulse || '',
            spo2: data.vital_signs?.spo2 || '',
            blood_pressure: data.vital_signs?.blood_pressure || '',
            respiration: data.vital_signs?.respiration || '',
            glucose_test: data.vital_signs?.glucose_test || '',
            time: data.vital_signs?.time || getCurrentTime(),
          },
          additional_vital_signs: data.additional_vital_signs || [],
          level_of_consciousness: data.level_of_consciousness || [],
          medication_during_transfer: med_transfer,
          infectious_precautions: data.infectious_precautions || [],
          s_situation: data.s_situation || '',
          b_background: data.b_background || '',
          a_assessment: data.a_assessment || '',
          r_recommendation: data.r_recommendation || '',
          status_during_transport: data.status_during_transport || '',
          handover_vital_signs: {
            temperature: data.handover_temp || '',
            pulse: data.handover_pulse || '',
            blood_pressure: data.handover_bp || '',
            respiration: data.handover_respiration || '',
            glucose_test: data.handover_glucose_test || '',
          },
          handover_acknowledgment_type: data.handover_acknowledgment_type || acknowledgmentType,
          acknowledged_received_items: data.acknowledged_received_items || [],
          received_by: data.received_by || '',
          received_position: data.received_position || '',
          received_id_number: data.received_id_number || '',
          manual_ackn_nurse_name: data.manual_ackn_nurse_name || '',
          manual_ackn_signature: data.manual_ackn_signature || null,
          signature: data.signature || null,
          mt_signature: data.mt_signature || null,
          medical_staff_involved: staff,
          ems_action_plan: data.ems_action_plan || [],
        });
        setStaffRows(staff.map((_, index) => ({ id: index + 1 })));
        setVitalSignCount((data.additional_vital_signs?.length || 0) + 1);
      } catch (error) {
        console.error('Error fetching patient situation:', error);
      }
    };
    if (caseId) {
      fetchPatientSituation();
    }
  }, [caseId]);

  // Initialize Bootstrap modal for the patient situation modal
  useEffect(() => {
    const modalElement = document.getElementById('bs-fullscreen');
    if (modalElement) {
      patientModalRef.current = new bootstrap.Modal(modalElement, { backdrop: 'static' });
    }

    // Cleanup on component unmount
    return () => {
      if (patientModalRef.current) {
        patientModalRef.current.hide();
        // Remove any residual backdrop
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
      }
    };
  }, []);

  // Handle acknowledgment type change
  const handleAcknowledgmentTypeChange = (type) => {
    if (!isEditable) return;

    setFormData((prev) => ({
      ...prev,
      handover_acknowledgment_type: type,
      ...(type === 'Automatically'
        ? {
            // Clear manual fields when switching to Automatic
            manual_ackn_nurse_name: '',
            manual_ackn_signature: null,
          }
        : {
            // Clear automatic fields when switching to Manual
            acknowledged_received_items: [],
            received_by: '',
            received_position: '',
            received_id_number: '',
            signature: null,
          }),
    }));
  };

  // Validate form data before submission
  const validateForm = () => {
    if (formData.past_medical_history.includes('Others') && !formData.past_medical_history_details['Others']) {
      setFormData((prev) => ({
        ...prev,
        past_medical_history: prev.past_medical_history.filter((item) => item !== 'Others'),
      }));
    }

    if (formData.past_medical_history.includes('Allergy to') && !formData.past_medical_history_details['Allergy to']) {
      setFormData((prev) => ({
        ...prev,
        past_medical_history: prev.past_medical_history.filter((item) => item !== 'Allergy to'),
      }));
    }

    if (!formData.clinical_condition) return 'Clinical Condition is mandatory';
    if (!formData.past_medical_history.length) return 'At least one Past Medical History option must be selected';
    for (const key of Object.keys(formData.vital_signs)) {
      if (key !== 'time' && !formData.vital_signs[key]) {
        return `Vital Signs: ${key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} is mandatory`;
      }
    }
    for (const vital of formData.additional_vital_signs) {
      for (const key of Object.keys(vital)) {
        if (key !== 'time' && !vital[key]) {
          return `Additional Vital Sign: ${key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} is mandatory`;
        }
      }
    }
    if (!formData.level_of_consciousness.length) return 'Level of Consciousness is mandatory';
    for (let i = 0; i < formData.medication_during_transfer.length; i++) {
      const med = formData.medication_during_transfer[i];
      if (med.medication_name && (!med.dose || !med.time)) {
        return `Medication ${i + 1}: Dose and Time are mandatory if Medication Name is provided`;
      }
    }
    if (!formData.infectious_precautions.length) return 'At least one Infectious Precaution must be selected';
    if (!formData.s_situation) return 'SBAR: Situation is mandatory';
    if (!formData.b_background) return 'SBAR: Background is mandatory';
    if (!formData.a_assessment) return 'SBAR: Assessment is mandatory';
    if (!formData.r_recommendation) return 'SBAR: Recommendation is mandatory';
    if (!formData.status_during_transport) return 'Status During Transport is mandatory';
    for (const key of Object.keys(formData.handover_vital_signs)) {
      if (!formData.handover_vital_signs[key]) {
        return `Handover Vital Signs: ${key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} is mandatory`;
      }
    }

    // Conditional validation based on acknowledgment type
    if (formData.handover_acknowledgment_type === 'Automatically') {
      if (!formData.acknowledged_received_items.length) return 'At least one Acknowledged Received Item must be selected';
      if (!formData.received_by) return 'Received By is mandatory';
      if (!formData.received_position) return 'Received Position is mandatory';
      if (!formData.received_id_number) return 'Received ID Number is mandatory';
      if (!formData.signature) return 'Signature is mandatory for Automatic Acknowledgment';
    } else if (formData.handover_acknowledgment_type === 'Manually') {
      if (!formData.manual_ackn_nurse_name) return 'Nurse Name is mandatory for Manual Acknowledgment';
      if (!formData.manual_ackn_signature) return 'Manual Acknowledgment Signature is mandatory';
      // Don't require main signature for Manual type
    }

    // Medical Transport signature is always required
    if (!formData.mt_signature) return 'Medical Transport Signature is mandatory';

    // Medical staff validation
    for (let i = 0; i < formData.medical_staff_involved.length; i++) {
      const staff = formData.medical_staff_involved[i];
      if (!staff.role) return `Medical Staff ${i + 1}: Role is mandatory`;
      if (!staff.signature) return `Medical Staff ${i + 1}: Signature is mandatory`;
    }

    return null;
  };

  // Handle adding a new vital sign
  const handleAddVitalSign = () => {
    if (!isEditable || vitalSignCount >= 4) {
      if (vitalSignCount >= 4) alert('Cannot add more than 4 vital sign entries');
      return;
    }
    const currentTime = getCurrentTime();
    setFormData((prev) => ({
      ...prev,
      additional_vital_signs: [...prev.additional_vital_signs, { pulse: '', spo2: '', blood_pressure: '', respiration: '', glucose_test: '', time: currentTime }],
    }));
    setVitalSignCount((prev) => prev + 1);
  };

  // Handle deleting a vital sign field
  const handleDeleteVitalSign = (index) => {
    if (!isEditable) return;
    setFormData((prev) => ({
      ...prev,
      additional_vital_signs: prev.additional_vital_signs.filter((_, i) => i !== index),
    }));
    setVitalSignCount((prev) => prev - 1);
  };

  // Handle saving a signature
  const handleSaveSignature = (signatureUrl, index) => {
    if (!isEditable) return;
    if (signatureModalType === 'main') {
      setFormData((prev) => ({ ...prev, signature: signatureUrl }));
    } else if (signatureModalType === 'mt') {
      setFormData((prev) => ({ ...prev, mt_signature: signatureUrl }));
    } else if (signatureModalType === 'medical_staff' && index !== null) {
      setFormData((prev) => ({
        ...prev,
        medical_staff_involved: prev.medical_staff_involved.map((staff, i) => (i === index ? { ...staff, signature: signatureUrl } : staff)),
      }));
    } else if (signatureModalType === 'manual_ackn') {
      setFormData((prev) => ({ ...prev, manual_ackn_signature: signatureUrl }));
    }
    setSignatureModalType(null);
    setSignatureModalIndex(null);
  };

  // Handle deleting a signature
  const handleDeleteSignature = (type, index = null) => {
    if (!isEditable) return;
    if (type === 'main') {
      setFormData((prev) => ({ ...prev, signature: null }));
    } else if (type === 'mt') {
      setFormData((prev) => ({ ...prev, mt_signature: null }));
    } else if (type === 'medical_staff' && index !== null) {
      setFormData((prev) => ({
        ...prev,
        medical_staff_involved: prev.medical_staff_involved.map((staff, i) => (i === index ? { ...staff, signature: null } : staff)),
      }));
    } else if (type === 'manual_ackn') {
      setFormData((prev) => ({ ...prev, manual_ackn_signature: null }));
    }
  };

  // Handle adding a new medication row
  const handleAddMedication = () => {
    if (!isEditable) return;
    setFormData((prev) => ({
      ...prev,
      medication_during_transfer: [...prev.medication_during_transfer, { medication_name: '', dose: '', time: '' }],
    }));
  };

  // Handle deleting a medication row
  const handleDeleteMedicationRow = (index) => {
    if (!isEditable || formData.medication_during_transfer.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      medication_during_transfer: prev.medication_during_transfer.filter((_, i) => i !== index),
    }));
  };

  // Handle deleting a staff row
  const handleDeleteStaffRow = (index) => {
    if (!isEditable || staffRows.length <= 1) return;
    setStaffRows((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      medical_staff_involved: prev.medical_staff_involved.filter((_, i) => i !== index),
    }));
  };

  // Add a new staff row
  const addStaffRow = () => {
    if (!isEditable) return;
    setStaffRows((prev) => [...prev, { id: prev.length + 1 }]);
    setFormData((prev) => ({
      ...prev,
      medical_staff_involved: [...prev.medical_staff_involved, { role: '', signature: null }],
    }));
  };

  // Handle input changes for various form sections
  const handleInputChange = (section, field, value, index = null) => {
    if (!isEditable) return;
    if (section === 'past_medical_history_details') {
      setFormData((prev) => ({
        ...prev,
        past_medical_history_details: { ...prev.past_medical_history_details, [field]: value },
      }));
    } else if (section === 'additional_vital_signs' && index !== null) {
      setFormData((prev) => ({
        ...prev,
        additional_vital_signs: prev.additional_vital_signs.map((vital, i) => (i === index ? { ...vital, [field]: value } : vital)),
      }));
    } else if (index !== null) {
      setFormData((prev) => ({
        ...prev,
        [section]: prev[section].map((item, i) => (i === index ? { ...item, [field]: value } : item)),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [section]: typeof prev[section] === 'object' && !Array.isArray(prev[section]) ? { ...prev[section], [field]: value } : { ...prev, [field]: value },
      }));
    }
  };

  // Handle changes to past medical history checkboxes
  const handlePastMedicalHistoryChange = (condition, checked) => {
    if (!isEditable) return;
    setFormData((prev) => {
      let newHistory = checked
        ? condition === 'None'
          ? ['None']
          : [...prev.past_medical_history.filter((item) => item !== 'None'), condition]
        : prev.past_medical_history.filter((item) => item !== condition && item !== 'None');
      return { ...prev, past_medical_history: newHistory };
    });
  };

  // Open the signature modal for a specific type and index
  const handleOpenSignatureModal = (type, index = null) => {
    if (!isEditable) return;
    setSignatureModalType(type);
    setSignatureModalIndex(index);
  };

  // Save the patient situation data
  const handleSave = async () => {
    if (!isEditable) {
      alert('You do not have permission to save this form');
      console.error('Permission error: User does not have permission to save the form', { sessionRole });
      return;
    }

    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }

    setLoading(true);
    try {
      // Prepare payload based on acknowledgment type
      const payload = {
        case_id: caseId,
        clinical_condition: formData.clinical_condition,
        past_medical_history: formData.past_medical_history.map((condition) => ({
          condition,
          value: condition === 'Hypertension' ? '45' : formData.past_medical_history_details[condition] || 'null',
        })),
        vital_signs: formData.vital_signs,
        additional_vital_signs: formData.additional_vital_signs,
        level_of_consciousness: formData.level_of_consciousness,
        medication_during_transfer: formData.medication_during_transfer,
        infectious_precautions: formData.infectious_precautions,
        s_situation: formData.s_situation,
        b_background: formData.b_background,
        a_assessment: formData.a_assessment,
        r_recommendation: formData.r_recommendation,
        status_during_transport: formData.status_during_transport,
        handover_temp: formData.handover_vital_signs.temperature,
        handover_pulse: formData.handover_vital_signs.pulse,
        handover_bp: formData.handover_vital_signs.blood_pressure,
        handover_respiration: formData.handover_vital_signs.respiration,
        handover_glucose_test: formData.handover_vital_signs.glucose_test,
        handover_acknowledgment_type: formData.handover_acknowledgment_type,
        handover_acknowledged: formData.handover_acknowledgment_type === 'Automatically' && formData.acknowledged_received_items.length > 0 ? 'Yes' : 'No',
        
        // Conditional fields based on acknowledgment type
        ...(formData.handover_acknowledgment_type === 'Automatically'
          ? {
              // Automatic acknowledgment fields
              acknowledged_received_items: formData.acknowledged_received_items,
              received_by: formData.received_by,
              received_position: formData.received_position,
              received_id_number: formData.received_id_number,
              signature: formData.signature,
              // Manual fields as null
              manual_ackn_nurse_name: null,
              manual_ackn_signature: null,
            }
          : {
              // Manual acknowledgment fields
              manual_ackn_nurse_name: formData.manual_ackn_nurse_name,
              manual_ackn_signature: formData.manual_ackn_signature,
              // Automatic fields as null/empty
              acknowledged_received_items: [],
              received_by: null,
              received_position: null,
              received_id_number: null,
              signature: null,
            }),
        
        // Always required fields
        mt_signature: formData.mt_signature,
        medical_staff_involved: formData.medical_staff_involved,
        ems_action_plan: formData.ems_action_plan,
      };

      const response = await axiosInstance.post('/ems/patient-situation', payload, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.status === 'success') {
        alert('Patient situation saved successfully');
        patientModalRef.current?.hide();
        // Remove modal backdrop and related classes
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        onSave?.(); // Trigger parent refresh
      }
    } catch (error) {
      alert('Error saving patient situation: ' + error.message);
      console.error('Error saving patient situation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    patientModalRef.current?.hide();
    // Remove modal backdrop and related classes
    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      backdrop.remove();
    }
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  };

  // Show loading while checking edit availability
  if (editCheckLoading) {
    return (
      <div className="modal fade patient-new-case" id="bs-fullscreen" tabIndex="-1" aria-labelledby="bs-example-modal-lg" aria-hidden="true">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-body d-flex justify-content-center align-items-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Checking edit permissions...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render the patient situation modal
  return (
    <>
      <style>{`
        .modal-fullscreen {
          width: 80% !important;
          height: auto !important;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          margin: auto;
        }
        .col-two-half {
          width: 20%;
          padding: 0 10px;
        }
        .form-check-label {text-transform: capitalize;}
        @media(max-width: 1299px){
          .form-section__title { font-size: 14px; }
          .patient-new-case label { font-size: 12px; }
          .patient-new-case .label { font-size: 12px; }
          .patient-new-case .modal-fullscreen { width: calc(100% - 40px) !important; }
        }
        @media(max-width: 767px) {
          .col-two-half { width: 33.33%; }
        }
        @media(max-width: 575px) {
          .col-two-half { width: 50%; }
        }
        a { cursor: pointer; }
        .delete-vital, .delete-signature, .delete-staff {
          cursor: ${isEditable ? 'pointer' : 'not-allowed'};
          color: ${isEditable ? 'red' : '#6c757d'};
          margin-left: 10px;
        }
        .signature-image {
          max-width: 200px;
          max-height: 100px;
          margin-top: 10px;
          border: 1px solid #ccc;
          cursor: ${isEditable ? 'pointer' : 'not-allowed'};
        }
        .signature-button {
          cursor: ${isEditable ? 'pointer' : 'not-allowed'};
          color: ${isEditable ? '#007bff' : '#6c757d'};
          text-decoration: ${isEditable ? 'underline' : 'none'};
          margin-right: 10px;
        }
        .signature-container { display: flex; align-items: center; }
        .form-control[readonly], .form-check-input[readonly] {
          background-color: #e9ecef;
          cursor: not-allowed;
        }
        .vital-signs-row { display: flex; flex-wrap: wrap; align-items: end; }
      `}</style>
      <div className="modal fade patient-new-case" id="bs-fullscreen" tabIndex="-1" aria-labelledby="bs-example-modal-lg" aria-hidden="true">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title h4" id="exampleModalFullscreenLabel">
                Patient Situation - New Case
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={handleClose}></button>
            </div>
            <div className="modal-body">
              {/* Clinical Condition Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Clinical Condition</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {['Life Threatening', 'Urgent', 'Non-Urgent'].map((condition) => (
                      <div className="col-two-half mb-2" key={condition}>
                        <div className="custom-control custom-radio">
                          <input
                            type="radio"
                            id={`customRadio${condition}`}
                            name="clinical_condition"
                            className="form-check-input"
                            checked={formData.clinical_condition === condition}
                            onChange={() => setFormData((prev) => ({ ...prev, clinical_condition: condition }))}
                            disabled={!isEditable}
                          />
                          <label className="form-check-label ms-2" htmlFor={`customRadio${condition}`}>
                            {condition}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Past Medical History Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Past Medical History</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {['Hypertension', 'Stroke', 'Seizures', 'COPD', 'Allergy to', 'Others', 'Diabetes', 'Cardiac', 'Asthma', 'None'].map((condition) => (
                      <div className="col-two-half mb-2" key={condition}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`customCheck${condition}`}
                            checked={formData.past_medical_history.includes(condition)}
                            onChange={(e) => handlePastMedicalHistoryChange(condition, e.target.checked)}
                            disabled={!isEditable}
                          />
                          <label className="form-check-label" htmlFor={`customCheck${condition}`}>
                            {condition}
                          </label>
                        </div>
                        {(condition === 'Allergy to' || condition === 'Others') && formData.past_medical_history.includes(condition) && (
                          <input
                            type="text"
                            className="form-control mt-2"
                            placeholder={`Specify ${condition}`}
                            value={formData.past_medical_history_details[condition]}
                            onChange={(e) => handleInputChange('past_medical_history_details', condition, e.target.value)}
                            readOnly={!isEditable}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vital Signs Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Vital Signs</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    <div className="vital-signs-row d-flex flex-wrap align-items-end mb-3">
                      {Object.keys(formData.vital_signs).map((key) => (
                        <div className="col-two-half mb-2" key={key}>
                          <label>{key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.vital_signs[key]}
                            onChange={(e) => handleInputChange('vital_signs', key, e.target.value)}
                            readOnly={key === 'time' || !isEditable}
                          />
                        </div>
                      ))}
                    </div>
                    {formData.additional_vital_signs.map((vital, index) => (
                      <div className="vital-signs-row d-flex flex-wrap align-items-end mb-3" key={index}>
                        {Object.keys(vital).map((key) => (
                          <div className="col-two-half mb-2" key={key}>
                            <label>{key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</label>
                            <input
                              type="text"
                              className="form-control"
                              value={vital[key]}
                              onChange={(e) => handleInputChange('additional_vital_signs', key, e.target.value, index)}
                              readOnly={key === 'time' || !isEditable}
                            />
                          </div>
                        ))}
                        {isEditable && (
                          <div className="col-two-half mb-2">
                            <a href="#" className="delete-vital" onClick={(e) => { e.preventDefault(); handleDeleteVitalSign(index); }}>
                              <i className="ti ti-trash text-danger fs-5"></i>
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                    {isEditable && vitalSignCount < 4 && (
                      <div className="col-two-half mb-2">
                        <a href="#" className="label" onClick={(e) => { e.preventDefault(); handleAddVitalSign(); }}>
                          <i className="ti ti-circle-plus text-primary fs-5"></i> Add Vital Sign
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Level of Consciousness Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Level of Consciousness</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {['Alert', 'Verbal', 'Painful Stimulation', 'Unresponsive'].map((level) => (
                      <div className="col-two-half mb-2" key={level}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`customCheck${level}`}
                            checked={formData.level_of_consciousness.includes(level)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                level_of_consciousness: checked ? [...prev.level_of_consciousness, level] : prev.level_of_consciousness.filter((item) => item !== level),
                              }));
                            }}
                            disabled={!isEditable}
                          />
                          <label className="form-check-label" htmlFor={`customCheck${level}`}>
                            {level}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Medication During Transfer Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Medication During Transfer</h5>
                </div>
                <div className="col-lg-9">
                  <div className="medication-container">
                    {formData.medication_during_transfer.map((med, index) => (
                      <div className="row medication-row mb-2 align-items-end" key={index}>
                        <div className="col-two-half mb-2">
                          <label>Medication Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={med.medication_name}
                            onChange={(e) => handleInputChange('medication_during_transfer', 'medication_name', e.target.value, index)}
                            readOnly={!isEditable}
                          />
                        </div>
                        <div className="col-two-half mb-2">
                          <label>Rate/Dose</label>
                          <input
                            type="text"
                            className="form-control"
                            value={med.dose}
                            onChange={(e) => handleInputChange('medication_during_transfer', 'dose', e.target.value, index)}
                            readOnly={!isEditable}
                          />
                        </div>
                        <div className="col-two-half mb-2">
                          <label>Time</label>
                          <input
                            type="text"
                            className="form-control"
                            value={med.time}
                            onChange={(e) => handleInputChange('medication_during_transfer', 'time', e.target.value, index)}
                            readOnly={!isEditable}
                          />
                        </div>
                        {isEditable && (
                          <div className="col-two-half mb-2">
                            <a href="#" className="delete-vital" onClick={(e) => { e.preventDefault(); handleDeleteMedicationRow(index); }}>
                              <i className="ti ti-trash text-danger fs-5"></i>
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                    {isEditable && (
                      <div className="row">
                        <div className="col-two-half mb-2">
                          <a href="#" className="add-more-medication pt-4 d-block" onClick={(e) => { e.preventDefault(); handleAddMedication(); }}>
                            <i className="ti ti-circle-plus text-primary"></i> Add More
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Infectious Precaution Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Infectious Precaution</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {['Standard', 'Droplet', 'Airborne', 'Maximum Contact'].map((precaution) => (
                      <div className="col-two-half mb-2" key={precaution}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`customCheck${precaution}`}
                            checked={formData.infectious_precautions.includes(precaution)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                infectious_precautions: checked ? [...prev.infectious_precautions, precaution] : prev.infectious_precautions.filter((item) => item !== precaution),
                              }));
                            }}
                            disabled={!isEditable}
                          />
                          <label className="form-check-label" htmlFor={`customCheck${precaution}`}>
                            {precaution} Precaution
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* SBAR Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Physical Assessment & Report (SBAR)</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {['s_situation', 'b_background', 'a_assessment', 'r_recommendation'].map((field) => (
                      <div className="col-two-half mb-2" key={field}>
                        <label>{field.split('_')[0].toUpperCase()} - {field.split('_')[1].replace(/\b\w/g, (l) => l.toUpperCase())}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData[field]}
                          onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                          readOnly={!isEditable}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Status During Transport Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Status During Transport:</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    <div className="col-12 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={formData.status_during_transport}
                        onChange={(e) => setFormData((prev) => ({ ...prev, status_during_transport: e.target.value }))}
                        readOnly={!isEditable}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Vital Signs During Handover Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Vital Signs During Handover</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {Object.keys(formData.handover_vital_signs).map((key) => (
                      <div className="col-two-half mb-2" key={key}>
                        <label>{key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.handover_vital_signs[key]}
                          onChange={(e) => handleInputChange('handover_vital_signs', key, e.target.value)}
                          readOnly={!isEditable}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Handover Acknowledgment Section */}
              <div className="row py-3">
                <div className="col-lg-12">
                  <h5 className="form-section__title"><b>Handover Acknowledgment</b></h5>
                </div>
              </div>

              {/* Acknowledgment Type Section */}
              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Acknowledgment Type</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {['Automatically', 'Manually'].map((type) => (
                      <div className="col-two-half mb-2" key={type}>
                        <div className="custom-control custom-radio">
                          <input
                            type="radio"
                            id={`ackType${type}`}
                            name="handover_acknowledgment_type"
                            className="form-check-input"
                            checked={formData.handover_acknowledgment_type === type}
                            onChange={() => handleAcknowledgmentTypeChange(type)}
                            disabled={!isEditable}
                          />
                          <label className="form-check-label ms-2" htmlFor={`ackType${type}`}>
                            {type}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Automatically Acknowledgment Fields */}
              {formData.handover_acknowledgment_type === 'Automatically' && (
                <>
                  <div className="row py-3">
                    <div className="col-lg-3">
                      <h5 className="form-section__title">I acknowledge that I have received the following</h5>
                    </div>
                    <div className="col-lg-9">
                      <div className="row">
                        {['Patient', 'Patient Valuables', 'Laboratory Report', 'Medication list', 'Medical Report'].map((item) => (
                          <div className="col-two-half mb-2" key={item}>
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`receivedItem${item}`}
                                checked={formData.acknowledged_received_items.includes(item)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setFormData((prev) => ({
                                    ...prev,
                                    acknowledged_received_items: checked ? [...prev.acknowledged_received_items, item] : prev.acknowledged_received_items.filter((i) => i !== item),
                                  }));
                                }}
                                disabled={!isEditable}
                              />
                              <label className="form-check-label" htmlFor={`receivedItem${item}`}>
                                {item}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="row py-3">
                    <div className="col-lg-3">
                      <h5 className="form-section__title">Received by</h5>
                    </div>
                    <div className="col-lg-9">
                      <div className="row">
                        <div className="col-12 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={formData.received_by}
                            onChange={(e) => setFormData((prev) => ({ ...prev, received_by: e.target.value }))}
                            readOnly={!isEditable}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row py-3">
                    <div className="col-lg-3">
                      <h5 className="form-section__title">Position</h5>
                    </div>
                    <div className="col-lg-9">
                      <div className="row">
                        <div className="col-12 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={formData.received_position}
                            onChange={(e) => setFormData((prev) => ({ ...prev, received_position: e.target.value }))}
                            readOnly={!isEditable}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row py-3">
                    <div className="col-lg-3">
                      <h5 className="form-section__title">I.D. no.</h5>
                    </div>
                    <div className="col-lg-9">
                      <div className="row">
                        <div className="col-12 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={formData.received_id_number}
                            onChange={(e) => setFormData((prev) => ({ ...prev, received_id_number: e.target.value }))}
                            readOnly={!isEditable}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row py-3">
                    <div className="col-lg-3">
                      <h5 className="form-section__title">Signature</h5>
                    </div>
                    <div className="col-lg-9">
                      <div className="row">
                        <div className="col-6 mb-2 signature-container">
                          {formData.signature && (
                            <img src={formData.signature} alt="Signature" className="signature-image" onClick={() => isEditable && handleOpenSignatureModal('main')} />
                          )}
                          {!formData.signature && isEditable && (
                            <a href="#" className="signature-button" onClick={(e) => { e.preventDefault(); handleOpenSignatureModal('main'); }}>
                              Add Signature
                            </a>
                          )}
                          {isEditable && formData.signature && (
                            <a href="#" className="delete-signature" onClick={(e) => { e.preventDefault(); handleDeleteSignature('main'); }}>
                              <i className="ti ti-trash text-danger fs-5"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Manually Acknowledgment Fields */}
              {formData.handover_acknowledgment_type === 'Manually' && (
                <>
                  <div className="row py-3">
                    <div className="col-lg-3">
                      <h5 className="form-section__title">Nurse Name</h5>
                    </div>
                    <div className="col-lg-9">
                      <div className="row">
                        <div className="col-12 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={formData.manual_ackn_nurse_name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, manual_ackn_nurse_name: e.target.value }))}
                            readOnly={!isEditable}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row py-3">
                    <div className="col-lg-3">
                      <h5 className="form-section__title">Signature</h5>
                    </div>
                    <div className="col-lg-9">
                      <div className="row">
                        <div className="col-6 mb-2 signature-container">
                          {formData.manual_ackn_signature && (
                            <img src={formData.manual_ackn_signature} alt="Manual Acknowledgment Signature" className="signature-image" onClick={() => isEditable && handleOpenSignatureModal('manual_ackn')} />
                          )}
                          {!formData.manual_ackn_signature && isEditable && (
                            <a href="#" className="signature-button" onClick={(e) => { e.preventDefault(); handleOpenSignatureModal('manual_ackn'); }}>
                              Add Signature
                            </a>
                          )}
                          {isEditable && formData.manual_ackn_signature && (
                            <a href="#" className="delete-signature" onClick={(e) => { e.preventDefault(); handleDeleteSignature('manual_ackn'); }}>
                              <i className="ti ti-trash text-danger fs-5"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Common Sections for both acknowledgment types */}
              <div className="row py-3">
                <div className="col-lg-12">
                  <h5 className="form-section__title"><b>Medical Transport Form Sent to Receiving Facility via System</b></h5>
                </div>
              </div>

              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Signature</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    <div className="col-6 mb-2 signature-container">
                      {formData.mt_signature && (
                        <img src={formData.mt_signature} alt="MT Signature" className="signature-image" onClick={() => isEditable && handleOpenSignatureModal('mt')} />
                      )}
                      {!formData.mt_signature && isEditable && (
                        <a href="#" className="signature-button" onClick={(e) => { e.preventDefault(); handleOpenSignatureModal('mt'); }}>
                          Add Signature
                        </a>
                      )}
                      {isEditable && formData.mt_signature && (
                        <a href="#" className="delete-signature" onClick={(e) => { e.preventDefault(); handleDeleteSignature('mt'); }}>
                          <i className="ti ti-trash text-danger fs-5"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">Medical Staff Involved</h5>
                </div>
                <div className="col-lg-9">
                  <div className="staff-container">
                    {staffRows.map((row, index) => (
                      <div className="row staff-row mb-2" key={row.id}>
                        <div className="col-sm-6 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Name or Role"
                            value={formData.medical_staff_involved[index]?.role || ''}
                            onChange={(e) => handleInputChange('medical_staff_involved', 'role', e.target.value, index)}
                            readOnly={!isEditable}
                          />
                        </div>
                        <div className="col-sm-4 col-10 mb-2 signature-container">
                          {formData.medical_staff_involved[index]?.signature && (
                            <img
                              src={formData.medical_staff_involved[index].signature}
                              alt="Staff Signature"
                              className="signature-image mt-0"
                              onClick={() => isEditable && handleOpenSignatureModal('medical_staff', index)}
                            />
                          )}
                          {!formData.medical_staff_involved[index]?.signature && isEditable && (
                            <a href="#" className="signature-button" onClick={(e) => { e.preventDefault(); handleOpenSignatureModal('medical_staff', index); }}>
                              Add Signature
                            </a>
                          )}
                          {isEditable && formData.medical_staff_involved[index]?.signature && (
                            <a href="#" className="delete-signature" onClick={(e) => { e.preventDefault(); handleDeleteSignature('medical_staff', index); }}>
                              <i className="ti ti-trash text-danger fs-5"></i>
                            </a>
                          )}
                        </div>
                        {isEditable && (
                          <div className="col-2 mb-2">
                            <div className="d-flex align-items-center">
                              <a href="#" className="add-staff me-2" onClick={(e) => { e.preventDefault(); addStaffRow(); }}>
                                <i className="ti ti-circle-plus text-primary fs-6"></i>
                              </a>
                              {staffRows.length > 1 && (
                                <a href="#" className="delete-staff" onClick={(e) => { e.preventDefault(); handleDeleteStaffRow(index); }}>
                                  <i className="ti ti-trash text-danger fs-5"></i>
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="row py-3">
                <div className="col-lg-3">
                  <h5 className="form-section__title">EMS Action Plan</h5>
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    {['No need for transport', 'No Patient Found', 'Patient Transported', 'Treated at the Scene', 'Patient refused Transfer & Treatment'].map((action) => (
                      <div className="col-two-half mb-2" key={action}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`customCheck${action}`}
                            checked={formData.ems_action_plan.includes(action)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                ems_action_plan: checked ? [...prev.ems_action_plan, action] : prev.ems_action_plan.filter((item) => item !== action),
                              }));
                            }}
                            disabled={!isEditable}
                          />
                          <label className="form-check-label" htmlFor={`customCheck${action}`}>
                            {action}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {isEditable && (
                <button type="button" className="btn btn-success d-flex align-items-center justify-content-center gap-2" onClick={handleSave} disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              )}
              <button type="button" className="btn bg-danger-subtle text-danger font-medium" data-bs-dismiss="modal" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <SignatureModal
        isOpen={signatureModalType !== null}
        onClose={() => {
          setSignatureModalType(null);
          setSignatureModalIndex(null);
          // Remove modal backdrop and related classes for SignatureModal
          const backdrop = document.querySelector('.modal-backdrop');
          if (backdrop) {
            backdrop.remove();
          }
          document.body.classList.remove('modal-open');
          document.body.style.removeProperty('overflow');
          document.body.style.removeProperty('padding-right');
        }}
        onSave={handleSaveSignature}
        index={signatureModalIndex}
        isEditable={isEditable}
      />
    </>
  );
};

export default PatientSituationModal;